import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Brain } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import QuestionManagementMenu from "@/components/admin/QuestionManagementMenu";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { DataTable } from "@/components/common/DataTable";
import { ListToolbar } from "@/components/common/ListToolbar";
import { DataPagination } from "@/components/common/DataPagination";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import { useUrlState } from "@/lib/urlState";
import { queryKeys } from "@/lib/queryKeys";
import { exportRowsAsCsv } from "@/lib/csv";
import { formatDistanceToNow } from "date-fns";

interface AILogRow {
  id: string;
  created_at: string;
  status: string;
  prompt: string;
  response: string;
  grade: string | null;
  question_count: number | null;
  user_id: string | null;
}

const STATUS_OPTIONS = ["all", "success", "error"] as const;

const AdminAILogs = () => {
  const url = useUrlState();
  const search = url.get("q") ?? "";
  const status = (url.get("status") ?? "all") as (typeof STATUS_OPTIONS)[number];

  const paginated = usePaginatedQuery<AILogRow>({
    queryKey: queryKeys.aiLogs.list({
      page: 0,
      pageSize: 25,
      filters: { search, status },
    }),
    table: "ai_request_logs",
    syncToUrl: true,
    orderBy: { column: "created_at", ascending: false },
    filters: (q) => {
      let next = q;
      if (status !== "all") next = next.eq("status", status);
      if (search) next = next.or(`prompt.ilike.%${search}%,response.ilike.%${search}%`);
      return next;
    },
  });

  const columns = useMemo<ColumnDef<AILogRow, unknown>[]>(
    () => [
      {
        accessorKey: "created_at",
        header: "When",
        cell: ({ row }) =>
          formatDistanceToNow(new Date(row.original.created_at), { addSuffix: true }),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge
            variant={row.original.status === "success" ? "default" : "destructive"}
          >
            {row.original.status}
          </Badge>
        ),
      },
      { accessorKey: "grade", header: "Grade" },
      { accessorKey: "question_count", header: "Qty" },
      {
        accessorKey: "prompt",
        header: "Prompt",
        cell: ({ row }) => (
          <div className="max-w-md truncate" title={row.original.prompt}>
            {row.original.prompt}
          </div>
        ),
      },
      {
        accessorKey: "response",
        header: "Response",
        cell: ({ row }) => (
          <div className="max-w-md truncate" title={row.original.response}>
            {row.original.response}
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex flex-col">
      <Header />
      <div className="flex-1 container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 flex items-center gap-2">
          <Brain className="h-8 w-8" />
          AI Generation Logs
        </h1>
        <QuestionManagementMenu />

        <Card>
            <CardHeader>
              <CardTitle>Requests</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ListToolbar
                search={search}
                onSearchChange={(v) => url.set({ q: v || null, page: 0 })}
                searchPlaceholder="Search prompt or response…"
                filters={
                  <Select
                    value={status}
                    onValueChange={(v) => url.set({ status: v, page: 0 })}
                  >
                    <SelectTrigger className="w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                }
                onExportCsv={() =>
                  exportRowsAsCsv(
                    "ai-logs.csv",
                    paginated.rows as unknown as Record<string, unknown>[],
                  )
                }
              />

              <DataTable
                columns={columns}
                data={paginated.rows}
                isLoading={paginated.isLoading}
                isError={paginated.isError}
                onRetry={paginated.refetch}
                emptyTitle="No AI requests yet"
                emptyDescription="Generated questions will appear here."
                getRowId={(row) => row.id}
              />

              <DataPagination
                page={paginated.page}
                pageSize={paginated.pageSize}
                total={paginated.total}
                pageCount={paginated.pageCount}
                onPageChange={paginated.setPage}
                onPageSizeChange={paginated.setPageSize}
                isFetching={paginated.isFetching}
              />
            </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default AdminAILogs;
